import React from "react";
import {
  getServerSideSplitTreatments,
  sendClientSideSplitTrackEvent,
} from "@leafly-com/split-next";
import { GetServerSideProps } from "next";
import { useRouter } from "next/router";

import ProductLandingPageFAQs from "fixtures/ProductLandingPageFAQs";

import { getProductMarketplaceCategories } from "api/requests/getProductMarketplaceCategories";
import getSponsoredArticles, {
  SponsoredArticle,
} from "api/requests/getSponsoredArticles";
import publicConfig from "config/public";
import {
  CONCENTRATES_SUBCATEGORY_MAP,
  EDIBLE_SUBCATEGORY_MAP,
  VAPING_SUBCATEGORY_MAP,
} from "constants/products";
import { SEO } from "custom-types/Directus";
import { ProductMarketplaceCategory } from "custom-types/Product";
import { getCountryCodeHeader } from "utils/requestContext";

import FAQs from "components/FAQ";
import ProductsCoinList from "components/products/CoinList/ProductsCoinList";
import { FeaturedProducts } from "components/products/FeaturedProducts";
import { HeroBanner } from "components/products/HeroBanner";
import { LazyLoadProductsCarouselWithData } from "components/products/LazyLoadProductsCarouselWithData";
import { hasIosParam } from "components/products/lib/paramCheck";
import { ProductBreadcrumbs } from "components/products/ProductCategoryBreadcrumbs";
import { ProductCategoryMetaTags } from "components/products/ProductCategoryMetaTags";
import { ProductCategoryNavigation } from "components/products/ProductCategoryNavigation";
import ProductsAd from "components/products/ProductsAd";
import { ProductsCarouselWithData } from "components/products/ProductsCarouselWithData";
import ProductsCopyBlocks from "components/products/ProductsCopyBlocks";
import ProductsHeroAd from "components/products/ProductsHeroAd";
import { SponsoredArticlesCarousel } from "components/products/SponsoredArticlesCarousel";
import SubcategoryCarousel from "components/products/SubcategoryCarousel";
import ProductSubcategoryLinks from "components/ProductSubcategoryLinks";

import getSeo from "../../api/requests/directus/getSeo";
import { getSplitKey } from "../../utils/split/getSplitKey";

export const CAROUSEL_ROUTE_MAP = {
  cartridges: "/products/concentrates/cartridges",
  concentrates: "/products/concentrates",
  edibles: "/products/edibles",
  flowers: "/products/cannabis/flower",
  prerolls: "/products/cannabis/prerolls",
  vapes: "/products/vaping",
} as const;

const DEFAULT_H1 = "THC products, CBD, weed, & more";
const DEFAULT_DESCRIPTION =
  "Whether you are just getting interested in weed, THC, CBD, and Delta 8 products or if you are a seasoned stoner with a favorite strain; find the perfect product that fits your needs.";
const DEFAULT_META_TITLE =
  "THC Products, CBD, Weed, & More Online and Near You";
const DEFAULT_META_DESCRIPTION =
  "Whether you are just getting interested in weed, THC, CBD, and Delta 8 products or if you are a seasoned stoner with a favorite strain; Leafly helps you find the perfect product that fits your needs.";
const DEFAULT_IMAGE = `${publicConfig.imgix.publicUrl}/products/category-heroes/product-heroes-v4-1920x500-all-products-1x.jpg`;

type ProductIndexProps = {
  categories: ProductMarketplaceCategory[];
  articles: SponsoredArticle[];
  pmpRootAndCategoryTreatment: "on" | "off" | "control";
  seo?: SEO;
};

const ProductIndex: React.FC<ProductIndexProps> = ({
  categories,
  articles,
  pmpRootAndCategoryTreatment,
  seo,
}) => {
  const router = useRouter();
  const isIos = hasIosParam(router);
  const pmpRootAndCategoryTestEnabled = pmpRootAndCategoryTreatment === "on";
  const splitKey = getSplitKey();

  return (
    <>
      <ProductCategoryMetaTags
        title={seo?.metaTitle || DEFAULT_META_TITLE}
        description={seo?.metaDescription || DEFAULT_META_DESCRIPTION}
        image={seo?.heroImage || DEFAULT_IMAGE}
        canonicalOverride={seo?.canonicalOverride || undefined}
      />

      <ProductCategoryNavigation categories={categories} />

      <HeroBanner
        imageSrc={seo?.heroImage || DEFAULT_IMAGE}
        title={seo?.h1 || DEFAULT_H1}
        subtitle={seo?.description || DEFAULT_DESCRIPTION}
        useUpdatedHeroStyling={pmpRootAndCategoryTestEnabled}
      />

      <ProductsHeroAd useUpdatedHeroStyling={pmpRootAndCategoryTestEnabled} />

      {pmpRootAndCategoryTestEnabled && (
        <ProductsCoinList className="container" title="Browse by category" />
      )}

      <FeaturedProducts
        className="my-xl md:my-section container"
        titleOverride={seo?.h2 || undefined}
      />
      <div className="container">
        <ProductsCarouselWithData
          carouselPath={CAROUSEL_ROUTE_MAP.flowers}
          header="Flower"
          filter={{ subcategory: "flower" }}
          {...(pmpRootAndCategoryTestEnabled
            ? {
                // * Setting mb to 0 here since there is already 12px padding at the bottom of the carousel
                carouselClassName: "mt-xl md:mt-section mb-0",
                seeMoreColor: "default",
                seeMoreOnClick: () => {
                  sendClientSideSplitTrackEvent(
                    {
                      eventType: "SHH-785_view_all_click",
                      properties: {
                        category: "flower",
                      },
                    },
                    splitKey,
                  );
                },
                seeMoreText: "view all flower",
              }
            : {})}
        />
        {pmpRootAndCategoryTestEnabled ? (
          <>
            <h4 className="uppercase mt-xs mb-md">Find more flower</h4>
            <ProductSubcategoryLinks category="cannabis" subcategory="flower" />
          </>
        ) : null}

        <ProductsCarouselWithData
          carouselPath={CAROUSEL_ROUTE_MAP.edibles}
          carouselClassName={pmpRootAndCategoryTestEnabled ? "mb-md mt-xl" : ""}
          header="Edibles"
          filter={{ category: "edibles" }}
          {...(pmpRootAndCategoryTestEnabled
            ? {
                seeMoreColor: "default",
                seeMoreOnClick: () => {
                  sendClientSideSplitTrackEvent(
                    {
                      eventType: "SHH-785_view_all_click",
                      properties: {
                        category: "edibles",
                      },
                    },
                    splitKey,
                  );
                },
                seeMoreText: "view all edibles",
              }
            : {})}
        />
        {pmpRootAndCategoryTestEnabled && (
          <SubcategoryCarousel
            category="edibles"
            categoryMap={EDIBLE_SUBCATEGORY_MAP}
            headingClassName="uppercase text-sm font-extrabold"
            title={`Browse edibles by category`}
          />
        )}

        <ProductsAd
          className="my-xxl md:my-section gam-rectangular-fixed-height"
          sizes={{ desktop: [728, 90], mobile: [320, 50] }}
          unit="product_upper"
        />

        {!isIos && (
          <LazyLoadProductsCarouselWithData
            height={350}
            offset={100}
            carouselPath={CAROUSEL_ROUTE_MAP.cartridges}
            header="Cartridges"
            filter={{ subcategory: "cartridges" }}
            {...(pmpRootAndCategoryTestEnabled
              ? {
                  seeMoreColor: "default",
                  seeMoreOnClick: () => {
                    sendClientSideSplitTrackEvent(
                      {
                        eventType: "SHH-785_view_all_click",
                        properties: {
                          category: "cartridges",
                        },
                      },
                      splitKey,
                    );
                  },
                  seeMoreText: "view all cartridges",
                }
              : {})}
          />
        )}

        <LazyLoadProductsCarouselWithData
          height={350}
          offset={100}
          carouselPath={CAROUSEL_ROUTE_MAP.prerolls}
          header="Pre-rolls"
          filter={{ subcategory: "prerolls" }}
          {...(pmpRootAndCategoryTestEnabled
            ? {
                seeMoreColor: "default",
                seeMoreText: "view all pre-rolls",
              }
            : {})}
        />

        <LazyLoadProductsCarouselWithData
          height={350}
          offset={100}
          carouselPath={CAROUSEL_ROUTE_MAP.concentrates}
          carouselClassName={pmpRootAndCategoryTestEnabled ? "mb-md mt-xl" : ""}
          header="Concentrates"
          filter={{ category: "concentrates" }}
          {...(pmpRootAndCategoryTestEnabled
            ? {
                seeMoreColor: "default",
                seeMoreOnClick: () => {
                  sendClientSideSplitTrackEvent(
                    {
                      eventType: "SHH-785_view_all_click",
                      properties: {
                        category: "concentrates",
                      },
                    },
                    splitKey,
                  );
                },
                seeMoreText: "view all concentrates",
              }
            : {})}
        />
        {pmpRootAndCategoryTestEnabled && (
          <SubcategoryCarousel
            category="concentrates"
            categoryMap={CONCENTRATES_SUBCATEGORY_MAP}
            headingClassName="uppercase text-sm font-extrabold"
            title={`Browse concentrates by category`}
          />
        )}

        <ProductsAd
          className="my-xxl md:my-section gam-rectangular-min-height"
          sizes={{
            desktop: [728, 90],
            mobile: [
              [320, 50],
              [300, 250],
            ],
          }}
          unit="product_mid"
        />

        {!isIos && (
          <LazyLoadProductsCarouselWithData
            height={350}
            offset={100}
            carouselPath={CAROUSEL_ROUTE_MAP.vapes}
            carouselClassName={
              pmpRootAndCategoryTestEnabled ? "mb-md mt-xl" : ""
            }
            header="Vaping"
            filter={{ category: "vaping" }}
            {...(pmpRootAndCategoryTestEnabled
              ? {
                  seeMoreColor: "default",
                  seeMoreOnClick: () => {
                    sendClientSideSplitTrackEvent(
                      {
                        eventType: "SHH-785_view_all_click",
                        properties: {
                          category: "vaping",
                        },
                      },
                      splitKey,
                    );
                  },
                  seeMoreText: "view all vaping",
                }
              : {})}
          />
        )}

        {!isIos && pmpRootAndCategoryTestEnabled && (
          <SubcategoryCarousel
            category="vaping"
            categoryMap={VAPING_SUBCATEGORY_MAP}
            headingClassName="uppercase text-sm font-extrabold"
            title={`Browse vaping by category`}
          />
        )}
      </div>

      <SponsoredArticlesCarousel
        className="my-xl md:my-section container"
        initialArticles={articles}
        sponsorId={0}
        sponsorType="category"
      />

      <ProductsCopyBlocks
        className="container my-xl md:my-section"
        blocks={seo?.copyBlocks}
      />

      <FAQs
        title={
          seo?.faqTitle ||
          "Frequently asked questions about buying cannabis products"
        }
        data={seo?.faqs || ProductLandingPageFAQs}
        initialShowPanel={true}
        className="my-xl md:my-section"
      />

      <ProductsAd
        className="my-xxl md:my-section gam-rectangular-min-height"
        sizes={{
          desktop: [728, 90],
          mobile: [
            [320, 50],
            [300, 250],
          ],
        }}
        unit="product_lower"
      />

      {pmpRootAndCategoryTestEnabled && (
        <div className="container">
          <ProductBreadcrumbs />
        </div>
      )}
    </>
  );
};

// @ts-expect-error -- TODO: Fix return type to adhere to props shape -- https://leafly.atlassian.net/browse/NL-1564
export const getServerSideProps: GetServerSideProps<ProductIndexProps> = async (
  context,
) => {
  const countryCode = getCountryCodeHeader(context.req.headers);

  const {
    "SHH-785_pmpRootAndCategory_test": {
      treatment: pmpRootAndCategoryTreatment,
    },
  } = await getServerSideSplitTreatments(
    ["SHH-785_pmpRootAndCategory_test"],
    getSplitKey(context),
  );

  const [{ categories }, articles, seo] = await Promise.all([
    getProductMarketplaceCategories(countryCode),
    getSponsoredArticles({ id: 0, sponsorType: "category" }),
    getSeo(context.resolvedUrl, countryCode),
  ]);

  return {
    props: {
      articles,
      categories,
      pmpRootAndCategoryTreatment,
      seo,
    },
  };
};

export default ProductIndex;
